import React, { useState, useEffect, useRef, useCallback } from 'react';
import usePageTitle from '@/hooks/usePageTitle';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Music, AlertCircle, Clock, CheckCircle, XCircle, Users, Lock, Maximize, Minimize, Printer, SkipForward, CreditCard } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import SpectatorPaymentScreen from '@/components/SpectatorPaymentScreen';
import SelfRegWizard from '@/pages/SelfRegWizard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PIN = '2507';

// Helper to get the proxied audio URL for a gymnast (avoids CORS issues)
const getFloorMusicUrl = (gymnastId) => {
  return `${API}/audio/floor-music/${gymnastId}`;
};

export default function RegistrationApp({ terminal = null }) {
  // Page title
  usePageTitle('Registration');
  
  // PIN Authentication — skipped for self-registration terminals (security is via /open lock)
  const [isAuthenticated, setIsAuthenticated] = useState(!!terminal);
  const [pinInput, setPinInput] = useState('');
  
  // Terminal Selection (selected at start of session)
  const [sessionTerminal, setSessionTerminal] = useState(null); // {terminalId, name}
  const [availableTerminals, setAvailableTerminals] = useState([]);
  // Skip SumUp terminal selection entirely for self-registration terminals — lock/unlock is the security
  const [terminalSelectionDone, setTerminalSelectionDone] = useState(!!terminal);
  const [loadingTerminals, setLoadingTerminals] = useState(false);
  
  // Shared state
  const [competitions, setCompetitions] = useState([]);
  const [selectedCompetition, setSelectedCompetition] = useState('');
  const [programme, setProgramme] = useState({ rounds: [] });
  const [selectedRound, setSelectedRound] = useState(null);
  
  // Club check-in wizard state
  const [wizardStep, setWizardStep] = useState(0); // 0=Not started, 1=Club, 2=Gymnast, 3=Print, 3.5=Apparatus, 4=Spectators, 5=Music, 6=Live Scores, 7=Phone, 8=Payment
  const [availableGymnasts, setAvailableGymnasts] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [selectedClub, setSelectedClub] = useState('');
  const [selectedGymnast, setSelectedGymnast] = useState(null);
  const [competingApparatus, setCompetingApparatus] = useState(['vault', 'bars', 'beam', 'floor', 'range']); // Default all selected
  const [spectatorCount, setSpectatorCount] = useState(0);
  const [spectatorSettings, setSpectatorSettings] = useState(null); // Spectator payment settings
  const [paymentResult, setPaymentResult] = useState(null); // Payment result from SumUp
  const [musicConfirmed, setMusicConfirmed] = useState(null); // true = correct, false = incorrect
  const [wantsLiveScores, setWantsLiveScores] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const audioRef = useRef(null);

  // ── Self-registration terminal state ────────────────────────────────────────
  const [isTerminalLocked, setIsTerminalLocked] = useState(false);
  const pendingAutoRoundRef = useRef(null);

  // Check PIN authentication
  const handlePinSubmit = (e) => {
    e.preventDefault();
    if (pinInput === PIN) {
      setIsAuthenticated(true);
      toast.success('Access granted');
    } else {
      toast.error('Incorrect PIN');
      setPinInput('');
    }
  };
  // Load terminals when authenticated (for terminal selection)
  useEffect(() => {
    if (isAuthenticated && !terminalSelectionDone) {
      loadAvailableTerminals();
    }
  }, [isAuthenticated, terminalSelectionDone]);

  // Load competitions after terminal selection is done
  useEffect(() => {
    if (isAuthenticated && terminalSelectionDone) {
      loadCompetitions();
    }
  }, [isAuthenticated, terminalSelectionDone]);

  // Load available terminals from SumUp settings
  const loadAvailableTerminals = async () => {
    setLoadingTerminals(true);
    try {
      const response = await axios.get(`${API}/registration/terminals`);
      const terminals = response.data.terminals || [];
      setAvailableTerminals(terminals);
      
      // Only auto-skip if NO terminals are configured at all
      if (terminals.length === 0) {
        setTerminalSelectionDone(true);
      }
      // If 1 or more terminals, always show the selection screen so staff can confirm
    } catch (error) {
      console.error('Failed to load terminals:', error);
      // If failed to load, just skip terminal selection
      setTerminalSelectionDone(true);
    } finally {
      setLoadingTerminals(false);
    }
  };

  // Handle terminal selection
  const handleTerminalSelect = (terminal) => {
    setSessionTerminal(terminal);
    setTerminalSelectionDone(true);
    toast.success(`Terminal "${terminal.name}" selected for this session`);
  };

  // Load programme when competition selected
  useEffect(() => {
    if (selectedCompetition) {
      loadProgramme();
    }
  }, [selectedCompetition]);

  // Load gymnasts when competition and round selected
  useEffect(() => {
    if (selectedCompetition && selectedRound) {
      loadGymnastsForRound();
    }
  }, [selectedCompetition, selectedRound]);

  // Auto-play music when gymnast selected (step 5 - music)
  // Note: We use the proxy URL which always fetches the CURRENT music from database
  useEffect(() => {
    if (wizardStep === 5 && selectedGymnast?.id && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      
      // Use proxy URL which fetches latest music from database
      // This ensures updated music is played even if cached data is stale
      const proxyUrl = getFloorMusicUrl(selectedGymnast.id);
      audioRef.current.src = proxyUrl;
      audioRef.current.load();
      
      setTimeout(() => {
        audioRef.current.play()
          .then(() => setIsPlaying(true))
          .catch(err => {
            console.error('Autoplay failed:', err);
            setIsPlaying(false);
          });
      }, 300);
    }
  }, [wizardStep, selectedGymnast]);

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  // ── Poll /open-state for self-registration terminals ────────────────────────
  useEffect(() => {
    if (!terminal) return;
    const lockKey = terminal === 'terminalone' ? 'terminalOneLocked' : 'terminalTwoLocked';
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/open-state`);
        const state = res.data;
        setIsTerminalLocked(!!(state[lockKey]));
        if (state.isActive && state.competitionId) {
          setSelectedCompetition(prev => {
            if (prev !== state.competitionId) {
              // Trigger programme load via useEffect on selectedCompetition
              pendingAutoRoundRef.current = state.roundNumber;
              return state.competitionId;
            }
            return prev;
          });
          // If competition already loaded, just update round if needed
          if (state.roundNumber) pendingAutoRoundRef.current = state.roundNumber;
        }
      } catch (_) {}
    };
    poll();
    const id = setInterval(poll, 3000);
    return () => clearInterval(id);
  }, [terminal]);

  // ── Auto-select round once programme loads (for self-reg terminals) ──────────
  useEffect(() => {
    if (!pendingAutoRoundRef.current || !programme.rounds?.length) return;
    const targetRound = programme.rounds.find(
      r => r.roundNumber.toString() === pendingAutoRoundRef.current.toString()
    );
    if (targetRound) {
      setSelectedRound(targetRound);
      pendingAutoRoundRef.current = null;
    }
  }, [programme.rounds]);

  const loadCompetitions = async () => {
    try {
      const response = await axios.get(`${API}/registration/competitions`);
      setCompetitions(response.data);
    } catch (error) {
      console.error('Failed to load competitions:', error);
      toast.error('Failed to load competitions');
    }
  };

  const loadProgramme = async () => {
    try {
      const response = await axios.get(`${API}/registration/programme/${selectedCompetition}`);
      setProgramme(response.data);
      // Also load spectator settings for this competition
      loadSpectatorSettings();
    } catch (error) {
      console.error('Failed to load programme:', error);
      toast.error('Failed to load programme');
    }
  };

  const loadSpectatorSettings = async () => {
    try {
      const response = await axios.get(`${API}/registration/competition/${selectedCompetition}/spectator-settings`);
      setSpectatorSettings(response.data);
    } catch (error) {
      console.error('Failed to load spectator settings:', error);
      // Don't show error to user, just disable payment feature
      setSpectatorSettings(null);
    }
  };

  const loadGymnastsForRound = async () => {
    try {
      console.log('Loading gymnasts for competition:', selectedCompetition, 'round:', selectedRound.roundNumber);
      const response = await axios.get(
        `${API}/registration/gymnasts-by-round/${selectedCompetition}/${selectedRound.roundNumber}`
      );
      console.log('API Response:', response.data);
      console.log('Number of gymnasts:', response.data.length);
      
      // Sort gymnasts alphabetically by name
      const sortedGymnasts = [...response.data].sort((a, b) => {
        const nameA = `${a.preferredFirstName || a.firstName} ${a.preferredLastName || a.lastName}`.toLowerCase();
        const nameB = `${b.preferredFirstName || b.firstName} ${b.preferredLastName || b.lastName}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });
      setAvailableGymnasts(sortedGymnasts);
      
      // Extract unique clubs
      const uniqueClubs = [...new Set(sortedGymnasts.map(g => g.clubId))];
      console.log('Unique clubs found:', uniqueClubs);
      
      const clubsData = uniqueClubs.map(clubId => {
        const gymnast = sortedGymnasts.find(g => g.clubId === clubId);
        return { id: clubId, name: gymnast.clubName };
      });
      
      // Sort clubs alphabetically by name
      const sortedClubs = clubsData.sort((a, b) => a.name.localeCompare(b.name));
      console.log('Clubs data (sorted):', sortedClubs);
      
      setClubs(sortedClubs);
      
      if (clubsData.length === 0) {
        toast.error('No entries found for this competition. Please make sure entries have been submitted.');
      }
    } catch (error) {
      console.error('Failed to load gymnasts:', error);
      console.error('Error details:', error.response?.data);
      toast.error('Failed to load gymnasts: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Map of wizard step → which competition toggle gates it (or null if always on).
  // Steps not listed are always shown.
  const STEP_TOGGLE_KEY = {
    3: 'checkInPrintNumberEnabled',
    3.5: 'checkInCompetingApparatusEnabled',
    4: 'checkInSpectatorsEnabled',
    5: 'checkInMusicEnabled',
    7: 'checkInPhoneEnabled',
  };
  const WIZARD_ORDER = [1, 2, 3, 3.5, 4, 5, 6, 7, 8];

  const isStepEnabled = (step) => {
    const key = STEP_TOGGLE_KEY[step];
    if (!key) return true;
    const comp = competitions.find(c => c.id === selectedCompetition);
    if (!comp) return true;
    return comp[key] !== false;
  };

  // Advance to the next enabled wizard step after `from`. Falls back to step 8
  // (or whatever the final enabled step is) if everything in between is disabled.
  const goToStepAfter = (from) => {
    const idx = WIZARD_ORDER.indexOf(from);
    for (let i = (idx >= 0 ? idx + 1 : 0); i < WIZARD_ORDER.length; i++) {
      const s = WIZARD_ORDER[i];
      if (isStepEnabled(s)) {
        setWizardStep(s);
        return;
      }
    }
    setWizardStep(WIZARD_ORDER[WIZARD_ORDER.length - 1]);
  };

  const handleSelectClub = (clubId) => {
    setSelectedClub(clubId);
    setWizardStep(2);
  };

  const handleSelectGymnast = (gymnast) => {
    setSelectedGymnast(gymnast);
    goToStepAfter(2); // → Print (or skip to next enabled)
  };

  const handlePrintNumber = async () => {
    if (!selectedGymnast || !selectedCompetition) return;
    
    setIsPrinting(true);
    try {
      // Open the PDF in a new tab - browser will trigger print dialog
      const pdfUrl = `${API}/registration/gymnast-number-pdf/${selectedCompetition}/${selectedGymnast.id}`;
      const printWindow = window.open(pdfUrl, '_blank');
      
      if (printWindow) {
        // Give the PDF time to load, then trigger print
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 500);
        };
      }
      
      toast.success('Print dialog opened');
      // Advance past print → next enabled step (skips disabled ones)
      setCompetingApparatus(['vault', 'bars', 'beam', 'floor', 'range']); // Reset to all selected
      goToStepAfter(3);
    } catch (error) {
      console.error('Print failed:', error);
      toast.error('Failed to generate print');
    } finally {
      setIsPrinting(false);
    }
  };

  const handleSkipPrint = () => {
    setCompetingApparatus(['vault', 'bars', 'beam', 'floor', 'range']); // Reset to all selected
    goToStepAfter(3); // → Apparatus or next enabled
  };

  const handleApparatusToggle = (apparatus) => {
    setCompetingApparatus(prev => {
      if (prev.includes(apparatus)) {
        return prev.filter(a => a !== apparatus);
      } else {
        return [...prev, apparatus];
      }
    });
  };

  const handleApparatusSubmit = () => {
    goToStepAfter(3.5); // → Spectators or next enabled
  };

  const handleSpectatorSubmit = () => {
    // Continue to music step (or next enabled). Payment for extra spectators is
    // still resolved at step 8.
    goToStepAfter(4);
  };

  const handlePaymentComplete = (paymentData) => {
    setPaymentResult(paymentData);
    // Payment is at the end, so complete check-in after payment
    submitCheckIn(phoneNumber ? `+44${phoneNumber}` : null, wantsLiveScores || false);
  };

  const handlePaymentBack = () => {
    setWizardStep(7); // Back to phone step
  };

  const handleMusicConfirmation = async (musicCorrect) => {
    if (!selectedGymnast) return;

    // Store music confirmation
    setMusicConfirmed(musicCorrect);

    // Stop music
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsPlaying(false);

    // Move to live scores question (or skip to next enabled)
    goToStepAfter(5);
  };

  const handleLiveScoresAnswer = (answer) => {
    setWantsLiveScores(answer);
    if (answer && isStepEnabled(7)) {
      setWizardStep(7); // Phone step
    } else {
      // Phone disabled or user declined → go to payment-or-complete
      goToPaymentOrComplete(null, !!answer);
    }
  };

  // Helper to determine if payment is needed and go there or complete
  const goToPaymentOrComplete = (phone, wantsScores) => {
    const extraSpectators = Math.max(0, spectatorCount - (spectatorSettings?.freeSpectators || 0));
    const extraSpectatorPrice = spectatorSettings?.extraSpectatorPrice || 0;
    
    if (extraSpectators > 0 && extraSpectatorPrice > 0 && !paymentResult) {
      setWizardStep(8); // Go to payment
    } else {
      submitCheckIn(phone, wantsScores);
    }
  };

  const handlePhoneSubmit = () => {
    if (!phoneNumber || phoneNumber.length !== 10) {
      toast.error('Please enter 10 digits');
      return;
    }
    // Combine +44 with the entered number
    const fullNumber = `+44${phoneNumber}`;
    goToPaymentOrComplete(fullNumber, true);
  };

  const submitCheckIn = async (phone, liveScores) => {
    try {
      await axios.post(`${API}/registration/check-in`, {
        competitionId: selectedCompetition,
        roundNumber: selectedRound.roundNumber,
        roundName: selectedRound.roundName,
        groupName: selectedGymnast.groupName || "",
        gymnastId: selectedGymnast.id,
        musicCorrect: musicConfirmed, // Use stored music confirmation
        spectatorCount: spectatorCount,
        phoneNumber: phone,
        wantsLiveScores: liveScores,
        competingApparatus: competingApparatus // Add apparatus selection
      });

      const displayName = `${selectedGymnast.preferredFirstName || selectedGymnast.firstName} ${selectedGymnast.preferredLastName || selectedGymnast.lastName}`;
      toast.success(
        `✓ ${displayName} successfully registered!`,
        { duration: 3000 }
      );

      // Reset wizard
      resetWizard();
      
      // Reload gymnasts
      await loadGymnastsForRound();
    } catch (error) {
      console.error('Check-in failed:', error);
      toast.error('Failed to register gymnast');
    }
  };

  const resetWizard = () => {
    setWizardStep(0);
    setSelectedClub('');
    setSelectedGymnast(null);
    // Keep apparatus from competition config — don't reset to hardcoded all-5
    setSpectatorCount(0);
    setPaymentResult(null);
    setMusicConfirmed(null);
    setWantsLiveScores(null);
    setPhoneNumber('');
    setIsPrinting(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
    setIsPlaying(false);
  };

  const getCompetitionName = () => {
    const comp = competitions.find(c => c.id === selectedCompetition);
    return comp ? comp.title : '';
  };

  const toggleFullScreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
        toast.success('Entered full screen mode');
      } else {
        await document.exitFullscreen();
        toast.success('Exited full screen mode');
      }
    } catch (error) {
      console.error('Failed to toggle fullscreen:', error);
      toast.error('Failed to toggle fullscreen');
    }
  };

  const filteredGymnasts = selectedClub
    ? availableGymnasts.filter(g => g.clubId === selectedClub && !g.checkInStatus?.checkedIn && !g.checkInStatus?.withdrawn)
    : [];

  // Terminal locked screen (for self-registration terminals controlled from /open)
  if (terminal && isTerminalLocked) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center gap-6">
        <img src="/scorz-logo.png" alt="Scorz" className="h-20 w-auto select-none" draggable={false} />
        <Lock className="w-24 h-24 text-gray-200" />
        <div className="text-center">
          <h1 className="text-4xl font-black text-gray-900">Registration Closed</h1>
          <p className="text-gray-400 mt-2 text-lg">This terminal is not currently available</p>
        </div>
        <p className="absolute bottom-6 text-xs text-gray-300 tracking-widest uppercase">scorz.co.uk</p>
      </div>
    );
  }

  // Self-registration terminals: bypass all selection screens, use /open state directly
  if (terminal) {
    if (!selectedCompetition || !selectedRound) {
      return (
        <div className="min-h-screen bg-white flex flex-col items-center justify-center gap-6 select-none">
          <img src="/scorz-logo.png" alt="Scorz" className="h-20 w-auto" draggable={false} />
          <div className="w-12 h-12 border-4 border-gray-200 border-t-black rounded-full animate-spin" />
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-800">Getting ready...</h1>
            <p className="text-gray-400 mt-2 text-lg">Waiting for the control desk to set up</p>
          </div>
        </div>
      );
    }
    return (
      <SelfRegWizard
        competitionId={selectedCompetition}
        round={selectedRound}
        competition={competitions.find(c => c.id === selectedCompetition)}
      />
    );
  }

  // PIN protection screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <Lock className="w-16 h-16 text-[#000000]" />
            </div>
            <CardTitle className="text-2xl">Registration Check-In</CardTitle>
            <CardDescription>Enter PIN to access registration system</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePinSubmit} className="space-y-4">
              <Input
                type="password"
                value={pinInput}
                onChange={(e) => setPinInput(e.target.value)}
                placeholder="Enter 4-digit PIN"
                className="text-center text-2xl tracking-widest h-16"
                maxLength={4}
                autoFocus
              />
              <Button type="submit" className="w-full bg-[#000000] hover:bg-[#333333] h-12">
                Access Registration
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Terminal Selection Screen (after PIN, before main app)
  if (!terminalSelectionDone) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg">
          <CardHeader className="text-center">
            <img src="/scorz-logo.png" alt="Scorz Logo" className="h-16 w-auto mx-auto mb-4" />
            <CardTitle className="text-2xl">Select Card Terminal</CardTitle>
            <CardDescription>
              Choose the card terminal you'll be using for this registration session
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {loadingTerminals ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-black mb-4"></div>
                <p className="text-gray-600">Loading terminals...</p>
              </div>
            ) : availableTerminals.length === 0 ? (
              <div className="text-center py-8">
                <CreditCard className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600 mb-4">No card terminals configured</p>
                <Button onClick={() => setTerminalSelectionDone(true)} className="bg-black hover:bg-gray-800">
                  Continue Without Terminal
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {availableTerminals.map((terminal) => (
                  <Button
                    key={terminal.terminalId}
                    onClick={() => handleTerminalSelect(terminal)}
                    variant="outline"
                    className="w-full h-20 text-lg flex items-center justify-between hover:bg-gray-50 hover:border-black"
                    data-testid={`terminal-btn-${terminal.terminalId}`}
                  >
                    <div className="flex items-center gap-4">
                      <CreditCard className="w-8 h-8" />
                      <div className="text-left">
                        <div className="font-semibold">{terminal.name}</div>
                        <div className="text-sm text-gray-500">ID: {terminal.terminalId}</div>
                      </div>
                    </div>
                    <div className="text-2xl">→</div>
                  </Button>
                ))}
                <Button
                  variant="ghost"
                  className="w-full text-gray-500 mt-2"
                  onClick={() => setTerminalSelectionDone(true)}
                  data-testid="skip-terminal-btn"
                >
                  Continue without terminal
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img src="/scorz-logo.png" alt="Scorz Logo" className="h-10 w-auto" />
              <div>
                <h1 className="text-lg font-bold text-black" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                  Registration Desk
                </h1>
                <p className="text-xs text-gray-600">
                  {getCompetitionName() || 'Competition Check-In System'}
                </p>
              </div>
            </div>
            <Button
              onClick={toggleFullScreen}
              className="bg-[#000000] hover:bg-[#333333]"
              size="sm"
            >
              {isFullScreen ? (
                <>
                  <Minimize className="w-4 h-4 mr-1" />
                  Exit
                </>
              ) : (
                <>
                  <Maximize className="w-4 h-4 mr-1" />
                  Full Screen
                </>
              )}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-4">
        {/* Competition & Round Selection */}
        <Card className="mb-4">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Select Competition & Round</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-2 block">Competition</label>
                <Select value={selectedCompetition} onValueChange={(val) => {
                  setSelectedCompetition(val);
                  setSelectedRound(null);
                  // Set default apparatus from competition config
                  const comp = competitions.find(c => c.id === val);
                  const compApparatus = comp?.competitionApparatus?.length
                    ? comp.competitionApparatus
                    : ['vault', 'bars', 'beam', 'floor', 'range'];
                  setCompetingApparatus(compApparatus);
                  resetWizard();
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select competition..." />
                  </SelectTrigger>
                  <SelectContent>
                    {competitions.map(comp => (
                      <SelectItem key={comp.id} value={comp.id}>
                        {comp.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Round</label>
                <Select
                  value={selectedRound?.roundNumber.toString()}
                  onValueChange={(val) => {
                    const round = programme.rounds.find(r => r.roundNumber.toString() === val);
                    setSelectedRound(round);
                    resetWizard();
                  }}
                  disabled={!selectedCompetition || programme.rounds.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select round..." />
                  </SelectTrigger>
                  <SelectContent>
                    {programme.rounds.map(round => (
                      <SelectItem key={round.roundNumber} value={round.roundNumber.toString()}>
                        {round.roundName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {selectedCompetition && selectedRound && wizardStep === 0 && (
              <Button
                onClick={() => setWizardStep(1)}
                className="w-full bg-[#000000] hover:bg-[#333333] h-14 text-xl font-bold"
              >
                Ready - Start Check-In
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Step-by-Step Wizard */}
        {selectedCompetition && selectedRound && wizardStep >= 1 && (
          <>
            {/* Progress Indicator */}
            <div className="mb-3">
              <div className="flex items-center justify-between text-xs">
                <div className={`flex-1 text-center ${wizardStep >= 1 ? 'text-[#000000] font-semibold' : 'text-gray-400'}`}>
                  Club
                </div>
                <div className={`flex-1 text-center ${wizardStep >= 2 ? 'text-[#000000] font-semibold' : 'text-gray-400'}`}>
                  Gymnast
                </div>
                <div className={`flex-1 text-center ${wizardStep >= 3 ? 'text-[#000000] font-semibold' : 'text-gray-400'}`}>
                  Print
                </div>
                <div className={`flex-1 text-center ${wizardStep >= 3.5 ? 'text-[#000000] font-semibold' : 'text-gray-400'}`}>
                  Apparatus
                </div>
                <div className={`flex-1 text-center ${wizardStep >= 4 ? 'text-[#000000] font-semibold' : 'text-gray-400'}`}>
                  Spectators
                </div>
                <div className={`flex-1 text-center ${wizardStep >= 5 ? 'text-[#000000] font-semibold' : 'text-gray-400'}`}>
                  Music
                </div>
                <div className={`flex-1 text-center ${wizardStep >= 6 ? 'text-[#000000] font-semibold' : 'text-gray-400'}`}>
                  Live Scores
                </div>
                {spectatorSettings?.sumupEnabled && spectatorSettings?.requirePaymentForExtraSpectators && (
                  <div className={`flex-1 text-center ${wizardStep >= 8 ? 'text-[#000000] font-semibold' : 'text-gray-400'}`}>
                    Payment
                  </div>
                )}
              </div>
              <div className="mt-1 h-1.5 bg-gray-200 rounded-full">
                <div
                  className="h-1.5 bg-[#000000] rounded-full transition-all duration-300"
                  style={{ width: `${(Math.min(wizardStep, 8) / 8) * 100}%` }}
                />
              </div>
            </div>

            {/* Step 1: Select Club */}
            {wizardStep === 1 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-xl">Step 1: Select Club</CardTitle>
                </CardHeader>
                <CardContent>
                  {clubs.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-gray-500">No clubs found for this round</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {clubs.map(club => (
                        <Button
                          key={club.id}
                          onClick={() => handleSelectClub(club.id)}
                          className="h-16 text-lg font-bold bg-white border-3 border-gray-300 text-black hover:border-[#000000] hover:bg-[#000000] hover:text-white transition-all"
                        >
                          {club.name}
                        </Button>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Step 2: Select Gymnast */}
            {wizardStep === 2 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-xl">Step 2: Select Gymnast</CardTitle>
                  <CardDescription>
                    Club: <span className="font-bold text-[#000000]">{clubs.find(c => c.id === selectedClub)?.name}</span>
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {filteredGymnasts.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-gray-500 mb-3">All gymnasts from this club have been checked in</p>
                      <Button
                        onClick={() => setWizardStep(1)}
                        variant="outline"
                        className="w-full h-12"
                      >
                        ← Back to Club Selection
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-2 mb-3 max-h-96 overflow-y-auto">
                        {filteredGymnasts.map(gymnast => (
                          <Button
                            key={gymnast.id}
                            onClick={() => handleSelectGymnast(gymnast)}
                            className="w-full h-14 justify-center bg-white border-3 border-gray-300 text-black hover:border-[#000000] hover:bg-[#000000] hover:text-white transition-all"
                          >
                            <div className="font-bold text-lg">
                              {gymnast.preferredFirstName || gymnast.firstName} {gymnast.preferredLastName || gymnast.lastName}
                            </div>
                          </Button>
                        ))}
                      </div>
                      <Button
                        onClick={() => setWizardStep(1)}
                        variant="outline"
                        className="w-full h-12"
                      >
                        ← Back
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Step 3: Print Gymnast Number */}
            {wizardStep === 3 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Printer className="w-5 h-5" />
                    Step 3: Print Gymnast Number
                  </CardTitle>
                  <CardDescription>
                    Print A5 number sheet for {selectedGymnast?.preferredFirstName || selectedGymnast?.firstName} {selectedGymnast?.preferredLastName || selectedGymnast?.lastName}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-[#000000] bg-opacity-10 p-6 rounded-lg text-center">
                    {selectedGymnast?.programmeNumber ? (
                      <div className="text-8xl font-bold mb-3">
                        {selectedGymnast.programmeNumber}
                      </div>
                    ) : (
                      <div className="text-2xl font-medium text-gray-500 mb-3 py-4">
                        No programme number assigned
                      </div>
                    )}
                    <p className="text-xl font-bold">
                      {selectedGymnast?.preferredFirstName || selectedGymnast?.firstName} {selectedGymnast?.preferredLastName || selectedGymnast?.lastName}
                    </p>
                    <p className="text-base text-gray-600">
                      {clubs.find(c => c.id === selectedClub)?.name}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <Button
                      onClick={handlePrintNumber}
                      disabled={isPrinting || !selectedGymnast?.programmeNumber}
                      className="h-20 text-lg font-bold bg-[#000000] hover:bg-[#333333] flex flex-col items-center justify-center disabled:opacity-50"
                    >
                      <Printer className="w-8 h-8 mb-1" />
                      {isPrinting ? 'Opening...' : 'Print Number'}
                    </Button>
                    <Button
                      onClick={handleSkipPrint}
                      variant="outline"
                      className="h-20 text-lg font-bold flex flex-col items-center justify-center"
                    >
                      <SkipForward className="w-8 h-8 mb-1" />
                      Skip Print
                    </Button>
                  </div>

                  {!selectedGymnast?.programmeNumber && (
                    <p className="text-sm text-amber-600 text-center">
                      This gymnast has no programme number assigned yet. You can skip printing or assign a number in the programme first.
                    </p>
                  )}

                  <Button
                    onClick={() => setWizardStep(2)}
                    variant="outline"
                    className="w-full h-12"
                  >
                    ← Back to Gymnast Selection
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Step 3.5: Apparatus Selection */}
            {wizardStep === 3.5 && (
              <Card>
                <CardHeader>
                  <CardTitle>Apparatus Selection</CardTitle>
                  <CardDescription>
                    What apparatus is {selectedGymnast?.preferredFirstName || selectedGymnast?.firstName} competing?
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="bg-gray-100 p-4 rounded-lg text-center">
                    <p className="text-xl font-bold">
                      {selectedGymnast?.preferredFirstName || selectedGymnast?.firstName} {selectedGymnast?.preferredLastName || selectedGymnast?.lastName}
                    </p>
                    <p className="text-gray-600">{clubs.find(c => c.id === selectedClub)?.name}</p>
                  </div>

                  <p className="text-sm text-gray-600 text-center">
                    All apparatus selected by default. Tap to deselect any apparatus the gymnast is NOT competing.
                  </p>

                  <div className="grid grid-cols-5 gap-2">
                    {(competitions.find(c => c.id === selectedCompetition)?.competitionApparatus?.length
                      ? competitions.find(c => c.id === selectedCompetition).competitionApparatus
                      : ['vault', 'bars', 'beam', 'floor', 'range']
                    ).map(appId => {
                      const labels = { vault: 'VAULT', bars: 'BARS', beam: 'BEAM', floor: 'FLOOR', range: 'RANGE' };
                      return (
                        <button
                          key={appId}
                          onClick={() => handleApparatusToggle(appId)}
                          className={`h-20 rounded-lg font-bold text-sm transition-all ${
                            competingApparatus.includes(appId)
                              ? 'bg-black text-white'
                              : 'bg-gray-200 text-gray-400'
                          }`}
                          data-testid={`apparatus-${appId}`}
                        >
                          {labels[appId] || appId.toUpperCase()}
                        </button>
                      );
                    })}
                  </div>

                  {competingApparatus.length === 0 && (
                    <p className="text-red-500 text-sm text-center">
                      Please select at least one apparatus
                    </p>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <Button
                      onClick={() => setWizardStep(3)}
                      variant="outline"
                      className="h-14"
                    >
                      ← Back
                    </Button>
                    <Button
                      onClick={handleApparatusSubmit}
                      disabled={competingApparatus.length === 0}
                      className="h-14 bg-black hover:bg-gray-800"
                    >
                      Continue →
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 4: Spectator Count */}
            {wizardStep === 4 && (
              <Card>
                <CardHeader>
                  <CardTitle>Step 4: Number of Spectators</CardTitle>
                  <CardDescription>
                    For {selectedGymnast?.firstName} {selectedGymnast?.lastName}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-[#000000] bg-opacity-10 p-4 rounded-lg">
                    <p className="text-lg font-semibold">
                      {selectedGymnast?.firstName} {selectedGymnast?.lastName}
                    </p>
                    <p className="text-sm text-gray-600">
                      {selectedGymnast?.category}
                    </p>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      How many spectators? (Fire Safety)
                    </label>
                    <Select value={spectatorCount.toString()} onValueChange={(val) => setSpectatorCount(parseInt(val))}>
                      <SelectTrigger className="h-14 text-lg">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">0 spectators</SelectItem>
                        <SelectItem value="1">1 spectator</SelectItem>
                        <SelectItem value="2">2 spectators</SelectItem>
                        <SelectItem value="3">3 spectators</SelectItem>
                        <SelectItem value="4">4 spectators</SelectItem>
                        <SelectItem value="5">5 spectators</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex gap-4">
                    <Button
                      onClick={() => setWizardStep(3)}
                      variant="outline"
                      className="flex-1 h-14 text-lg"
                    >
                      ← Back
                    </Button>
                    <Button
                      onClick={handleSpectatorSubmit}
                      className="flex-1 bg-[#000000] hover:bg-[#333333] h-14 text-lg font-bold"
                      data-testid="spectators-continue-btn"
                    >
                      {spectatorSettings?.sumupEnabled && 
                       spectatorSettings?.requirePaymentForExtraSpectators &&
                       spectatorCount > (spectatorSettings?.includedSpectators || 0) &&
                       (spectatorSettings?.extraSpectatorPriceGbp || 0) > 0 ? (
                        <>
                          <CreditCard className="w-5 h-5 mr-2" />
                          Continue to Payment →
                        </>
                      ) : (
                        'Continue to Music →'
                      )}
                    </Button>
                  </div>

                  {/* Show payment preview if applicable */}
                  {spectatorSettings?.sumupEnabled && 
                   spectatorSettings?.requirePaymentForExtraSpectators &&
                   (spectatorSettings?.extraSpectatorPriceGbp || 0) > 0 && (
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-sm text-blue-800">
                        <strong>Spectator Info:</strong> {spectatorSettings.includedSpectators} spectator(s) included with entry.
                        {spectatorCount > spectatorSettings.includedSpectators && (
                          <span className="block mt-1 text-orange-700">
                            Extra spectators: {spectatorCount - spectatorSettings.includedSpectators} × £{spectatorSettings.extraSpectatorPriceGbp.toFixed(2)} = 
                            <strong> £{((spectatorCount - spectatorSettings.includedSpectators) * spectatorSettings.extraSpectatorPriceGbp).toFixed(2)}</strong>
                          </span>
                        )}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Step 8: Spectator Payment (SumUp Solo) - Now at the END */}
            {wizardStep === 8 && selectedGymnast && spectatorSettings && (
              <SpectatorPaymentScreen
                gymnast={selectedGymnast}
                club={clubs.find(c => c.id === selectedClub) || { id: selectedClub, name: 'Unknown Club' }}
                competition={competitions.find(c => c.id === selectedCompetition)}
                spectatorCount={spectatorCount}
                spectatorSettings={spectatorSettings}
                sessionTerminal={sessionTerminal}
                onPaymentComplete={handlePaymentComplete}
                onBack={handlePaymentBack}
              />
            )}

            {/* Step 5: Music Confirmation */}
            {wizardStep === 5 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Music className="w-5 h-5" />
                    Step 5: Confirm Floor Music
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {selectedGymnast?.floorMusicUrl ? (
                    <>
                      <div className="bg-[#000000] bg-opacity-10 p-6 rounded-lg text-center">
                        <Music className="w-20 h-20 mx-auto text-[#000000] mb-4 animate-pulse" />
                        <p className="text-lg font-semibold mb-2">
                          {selectedGymnast.firstName} {selectedGymnast.lastName}
                        </p>
                        <p className="text-sm text-gray-600 mb-4">
                          Spectators: {spectatorCount}
                        </p>
                        <div className="flex items-center justify-center gap-2 text-lg font-semibold mb-4">
                          {isPlaying ? (
                            <>
                              <div className="w-3 h-3 bg-green-600 rounded-full animate-pulse"></div>
                              Music Playing
                            </>
                          ) : (
                            <>
                              <Clock className="w-4 h-4" />
                              Ready to Play
                            </>
                          )}
                        </div>
                        
                        {/* Manual Play/Pause Button */}
                        <Button
                          onClick={() => {
                            if (audioRef.current && selectedGymnast?.id) {
                              if (isPlaying) {
                                audioRef.current.pause();
                              } else {
                                const proxyUrl = getFloorMusicUrl(selectedGymnast.id);
                                audioRef.current.src = proxyUrl;
                                audioRef.current.play()
                                  .then(() => setIsPlaying(true))
                                  .catch(err => {
                                    console.error('Play failed:', err);
                                    toast.error('Failed to play music. Please check the file.');
                                  });
                              }
                            }
                          }}
                          variant="outline"
                          className="px-8 py-2"
                        >
                          {isPlaying ? 'Pause Music' : 'Play Music'}
                        </Button>
                      </div>

                      <audio
                        ref={audioRef}
                        preload="auto"
                        onPlay={() => setIsPlaying(true)}
                        onPause={() => setIsPlaying(false)}
                        onEnded={() => setIsPlaying(false)}
                        style={{ display: 'none' }}
                      />

                      <div className="grid grid-cols-2 gap-6">
                        <Button
                          onClick={() => handleMusicConfirmation(true)}
                          className="bg-green-600 hover:bg-green-700 h-32 text-xl font-bold flex flex-col items-center justify-center"
                        >
                          <CheckCircle className="w-12 h-12 mb-2" />
                          ✓ Music is Correct
                        </Button>
                        <Button
                          onClick={() => handleMusicConfirmation(false)}
                          className="bg-gray-900 hover:bg-black h-32 text-xl font-bold flex flex-col items-center justify-center"
                        >
                          <XCircle className="w-12 h-12 mb-2" />
                          ✗ Music NOT Correct
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      <Alert className="bg-yellow-50 border-yellow-400">
                        <AlertCircle className="h-5 w-5 text-yellow-600" />
                        <AlertDescription className="text-yellow-800">
                          No floor music uploaded for this gymnast.
                        </AlertDescription>
                      </Alert>

                      <Button
                        onClick={() => handleMusicConfirmation(true)}
                        className="w-full bg-[#000000] hover:bg-[#333333]"
                      >
                        Continue Anyway
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Step 6: Live Scores Question */}
            {wizardStep === 6 && (
              <Card>
                <CardHeader>
                  <CardTitle>Step 6: Live Scores</CardTitle>
                  <CardDescription>
                    Would you like to receive live scores via text?
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-blue-50 p-4 rounded-lg text-center">
                    <p className="text-lg font-semibold mb-2">
                      {selectedGymnast?.firstName} {selectedGymnast?.lastName}
                    </p>
                    <p className="text-sm text-gray-600">
                      Receive real-time score updates via SMS
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <Button
                      onClick={() => handleLiveScoresAnswer(true)}
                      className="bg-green-600 hover:bg-green-700 h-28 text-xl font-bold"
                    >
                      <CheckCircle className="w-8 h-8 mr-2" />
                      YES, Send Scores
                    </Button>
                    <Button
                      onClick={() => handleLiveScoresAnswer(false)}
                      className="bg-gray-600 hover:bg-gray-700 h-28 text-xl font-bold"
                    >
                      <XCircle className="w-8 h-8 mr-2" />
                      NO Thanks
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 7: Phone Number */}
            {wizardStep === 7 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl">Step 7: Enter Phone Number</CardTitle>
                  <CardDescription className="text-lg">
                    We&apos;ll send live scores to this number
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-lg font-semibold">
                      {selectedGymnast?.preferredFirstName || selectedGymnast?.firstName} {selectedGymnast?.preferredLastName || selectedGymnast?.lastName}
                    </p>
                    <p className="text-sm text-gray-600">
                      Enter mobile number for live score updates
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="bg-gray-100 border-2 border-gray-300 rounded-md px-4 h-16 flex items-center justify-center text-2xl font-bold text-gray-700">
                      +44
                    </div>
                    <Input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, ''); // Only digits
                        if (value.length <= 10) {
                          setPhoneNumber(value);
                        }
                      }}
                      placeholder="7XXX XXXXXX"
                      className="text-2xl h-16 text-center flex-1"
                      maxLength={10}
                      autoFocus
                    />
                  </div>
                  
                  <p className="text-sm text-gray-500 text-center">
                    Enter 10 digits (without the leading 0)
                  </p>

                  <div className="flex gap-4">
                    <Button
                      onClick={() => setWizardStep(6)}
                      variant="outline"
                      className="flex-1 h-14 text-lg"
                    >
                      ← Back
                    </Button>
                    <Button
                      onClick={handlePhoneSubmit}
                      className="flex-1 bg-[#000000] hover:bg-[#333333] h-14 text-lg font-bold"
                      disabled={phoneNumber.length !== 10}
                    >
                      Complete Registration
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </main>
    </div>
  );
}
